import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db6fm53ghk82orf0cl30";

const CONTENT_SCOPES = [
  {
    icon: "ri-film-line",
    title: "电影内容",
    desc: "收录剧情、悬疑、爱情、喜剧等类型影片的简介与剧情脉络，方便选片参考。",
  },
  {
    icon: "ri-tv-2-line",
    title: "电视剧内容",
    desc: "涵盖都市、情感、悬疑、治愈等剧集内容，跟进更新节奏与系列信息。",
  },
  {
    icon: "ri-book-open-line",
    title: "剧情介绍",
    desc: "整理作品的故事梗概与关键信息，帮助你在观看前快速了解作品。",
  },
  {
    icon: "ri-compass-3-line",
    title: "筛选与推荐",
    desc: "通过类型、地区、年份筛选，并结合热度为你推荐风格相近的作品。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 岳母的诱惑",
    description:
      "了解岳母的诱惑影视内容站点的定位、内容范围与联系方式，我们专注电影与电视剧的剧情介绍与观看推荐。",
    keywords: "岳母的诱惑,关于我们,影视网站,剧情介绍,联系方式",
  });

  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("sending");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          (typeof serverMsg === "string" && serverMsg.trim() && !isSpam
            ? serverMsg
            : "") || "提交失败，请稍后重试或更换内容再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <Layout>
      <section className="bg-background-900/40 border-b border-background-800">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-black text-foreground-50">
            关于岳母的诱惑
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-3xl leading-relaxed">
            一个专注于电影与电视剧内容的影视门户，为影视爱好者整理作品简介、剧情介绍与观看推荐，帮助你更高效地发现想看的作品。
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
              网站介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-300 leading-loose text-justify">
              「岳母的诱惑」围绕电影与电视剧两大内容方向，收录剧集与影片的题材、地区、年份、更新情况等信息，并配套详尽的剧情介绍与相关推荐。我们希望用清晰的信息层级，让浏览影视内容这件事变得更简单、更直观。
            </p>
            <p className="text-sm md:text-base text-foreground-300 leading-loose text-justify">
              站内所有作品条目均配有独立的详情页面，你可以从首页、分类页、搜索结果页或多个推荐入口进入，查看作品剧情、主创信息，并通过标签前往相近风格的分类进行延伸浏览。喜欢的内容可以一键收藏，收藏记录仅保存在你的本机浏览器中。
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-loose text-justify">
              需要说明的是，本站目前仅提供剧情介绍与信息浏览能力，<strong className="text-foreground-200">暂不提供视频播放、下载及任何付费功能</strong>，页面中的相关入口仅用于展示信息结构，为后续可能的扩展预留空间。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {CONTENT_SCOPES.map((item) => (
                <div
                  key={item.title}
                  className="p-4 md:p-5 rounded-lg bg-background-900/60 border border-background-800"
                >
                  <span className="w-10 h-10 rounded-md bg-primary-950 text-accent-400 flex items-center justify-center">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h4 className="mt-3 font-medium text-foreground-100">
                    <Link to="/category" className="hover:text-accent-400 transition-colors">
                      {item.title}
                    </Link>
                  </h4>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="p-5 rounded-lg bg-background-900/60 border border-background-800 lg:sticky lg:top-24">
              <h2 className="font-heading text-base font-bold text-foreground-100 mb-4">
                站点信息
              </h2>
              <dl className="space-y-3 text-sm">
                {[
                  { label: "站点名称", value: "岳母的诱惑" },
                  { label: "内容方向", value: "电影 / 电视剧" },
                  { label: "内容语言", value: "简体中文" },
                  { label: "联系方式", value: "feedback@yuemudeyouhuo.example" },
                  { label: "服务范围", value: "剧情介绍 · 信息浏览" },
                ].map((row) => (
                  <div key={row.label} className="flex flex-col gap-0.5">
                    <dt className="text-xs text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-200 break-all">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-accent-400 hover:text-accent-300 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页浏览影视
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section id="contact" className="bg-background-900/40 border-y border-background-800 scroll-mt-24">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                联系与意见反馈
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-300 leading-relaxed">
                如果你对内容有建议、发现信息有误，或希望我们补充某些作品，欢迎通过下方表单告诉我们。我们会在收到反馈后尽快处理。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-950 text-accent-400 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈邮箱</p>
                    <p className="text-sm text-foreground-200">feedback@yuemudeyouhuo.example</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-950 text-accent-400 flex items-center justify-center shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">处理时间</p>
                    <p className="text-sm text-foreground-200">工作日 1-3 个工作日内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 md:p-6 rounded-lg bg-background-950 border border-background-800">
              {status === "success" ? (
                <div className="py-10 text-center">
                  <span className="w-14 h-14 mx-auto rounded-full bg-accent-500/15 text-accent-400 flex items-center justify-center mb-4">
                    <i className="ri-check-line text-3xl"></i>
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-50">
                    反馈已提交，感谢你的建议
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500">
                    我们会认真阅读每一条反馈，并尽快跟进处理。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                  >
                    再提交一条
                  </button>
                </div>
              ) : (
                <form
                  id="ymdh-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="email" className="block text-xs text-foreground-400 mb-1.5">
                      你的邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-3 py-2.5 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-100 placeholder:text-foreground-600 outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="topic" className="block text-xs text-foreground-400 mb-1.5">
                      反馈类型
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full px-3 py-2.5 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-200 outline-none focus:border-primary-500 cursor-pointer"
                    >
                      <option>内容建议</option>
                      <option>信息纠错</option>
                      <option>作品补充</option>
                      <option>合作咨询</option>
                      <option>其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs text-foreground-400 mb-1.5">
                      留言内容（最多 500 字）
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你的建议或问题…"
                      className="w-full px-3 py-2.5 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-100 placeholder:text-foreground-600 outline-none focus:border-primary-500 transition-colors resize-none"
                    />
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="form-extra-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="text-xs text-primary-300 bg-primary-950/60 border border-primary-800 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {status === "sending" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        提交反馈
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="disclaimer" className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16 scroll-mt-24">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-5">
          版权与免责声明
        </h2>
        <div className="p-5 md:p-6 rounded-lg bg-background-900/60 border border-background-800 space-y-4 text-sm text-foreground-400 leading-loose">
          <p>
            1. 本站为影视内容信息展示平台，页面中的影片名称、海报、剧情简介、主创信息等均为虚构示例内容，仅用于产品功能演示，不对应任何真实影视作品。
          </p>
          <p>
            2. 本站<strong className="text-foreground-200">不提供任何视频播放、下载服务</strong>，也不提供付费观看或交易功能，页面中相应描述仅为功能规划说明。
          </p>
          <p>
            3. 用户通过反馈表单提交的信息仅用于处理建议与问题，本站不会将相关信息用于其他用途。收藏等功能的数据仅保存在用户本机浏览器中。
          </p>
          <p>
            4. 如本站内容无意中涉及第三方权益，请通过上方联系方式与我们沟通，我们将及时核实并处理。
          </p>
        </div>
      </section>
    </Layout>
  );
}